import React, { useMemo } from 'react';
import styled from 'styled-components';

import { FixedFee, GUID, PricingPackage, TieredFee } from '@ndvr/billing-api';

import { formatMoney } from 'utils';
import { Text, VerticalLayout } from 'widgets';

type Alignment = 'start' | 'center' | 'end';

interface Props {
    pricingPackages?: PricingPackage[];
    pricingPackageGUID?: GUID<PricingPackage>;
    fixedFee?: FixedFee;
    annualBps?: NumberStr;
    alignment?: Alignment;
}

export const PricingInfo = ({
    pricingPackages,
    pricingPackageGUID,
    fixedFee,
    annualBps,
    alignment = 'start',
}: Props) => {
    const [activeTieredFee, activeFixedFee, activeAnnualBps] = useMemo(() => {
        if (!pricingPackageGUID) {
            return [undefined, fixedFee, annualBps];
        }
        const selectedPackage = pricingPackages?.find(
            (pricingPackage) => pricingPackage.pricingPackageGUID === pricingPackageGUID,
        );
        return [selectedPackage?.tieredFee, selectedPackage?.fixedFee, selectedPackage?.annualBps];
    }, [pricingPackages, pricingPackageGUID, fixedFee, annualBps]);

    return (
        <Container $alignment={alignment}>
            {activeFixedFee && (
                <Text largeBody>
                    {activeFixedFee.money.amount ? formatMoney(activeFixedFee.money) : '---'}/month
                    {activeFixedFee.maxAnnualBps && ' (max ' + activeFixedFee.maxAnnualBps + 'bps/year)'}
                </Text>
            )}
            {activeTieredFee ? (
                <Text largeBody>{getTieredBpsRangeLabel(activeTieredFee)}</Text>
            ) : (
                <Text largeBody>{activeAnnualBps ? activeAnnualBps + 'bps' : '---'}/year</Text>
            )}
        </Container>
    );
};

const getTieredBpsRangeLabel = (tieredFee: TieredFee) => {
    const bps = Object.values(tieredFee.tieredBps).map(Number);
    const minBps = Math.min(...bps);
    const maxBps = Math.max(...bps);
    return `${minBps}-${maxBps} bps/year`;
};

const Container = styled(VerticalLayout)<{ $alignment: Alignment }>`
    align-items: ${({ $alignment }) => $alignment};
`;
